/**
 * Authoritative geometry for the Narragansett-area realistic exercise (illustrative).
 * Coordinates: WGS84 decimal degrees; polygons as [lon, lat] rings (GeoJSON order).
 */

const closeRing = (ring) => {
  if (!ring || ring.length < 3) return ring;
  const [fx, fy] = ring[0];
  const [lx, ly] = ring[ring.length - 1];
  if (fx === lx && fy === ly) return ring;
  return [...ring, [fx, fy]];
};

/** Operational Area Alpha (6 vertices, closed). */
export const OP_AREA_ALPHA_RING = closeRing([
  [-71.32482, 41.5319],
  [-71.32222, 41.53443],
  [-71.3139, 41.53697],
  [-71.31325, 41.5362],
  [-71.31382, 41.53493],
  [-71.31608, 41.53248]
]);

/** Bounds to frame the exercise (padding applied in fitBounds if used). */
export const EXERCISE_SCENARIO_BOUNDS = {
  west: -71.352,
  east: -71.308,
  south: 41.518,
  north: 41.539
};

/**
 * Vessel tracks from live surface-vessel data in this exercise area.
 * Keys must match `unique_identifier` on each row (after normalization in dataService).
 * UUIDs updated from runtime hint (points in EXERCISE_SCENARIO_BOUNDS); reorder labels if your pipeline maps roles differently.
 * Optional remap: `REACT_APP_EXERCISE_VESSEL_ROLE_ALIAS_JSON` (see `exerciseVesselRoleLookup.js`).
 */
export const EXERCISE_VESSEL_ROLES = {
  '3461cf59-7676-40d9-9b48-421dc19abf12': {
    label: 'Threat USV (unknown_surface_usv)',
    color: [220, 38, 38, 230],
    headFill: [248, 113, 113, 255],
    width: 4
  },
  '83b1b84b-35a4-4d52-a6bb-bf8e2d603034': {
    label: 'Defense USV-1',
    color: [129, 123, 230, 220],
    headFill: [129, 123, 230, 230],
    width: 4
  },
  '2db628f8-4a68-4e17-b6bf-cf15a22694b3': {
    label: 'AUV (autonomous) — track 3',
    color: [227, 142, 37, 220],
    headFill: [227, 142, 37, 230],
    width: 3
  },
  '539acd93-0c4a-4584-b7ca-0bcd017f93d5': {
    label: 'Defense USV-3 (defensive_surface_usv)',
    color: [129, 123, 230, 220],
    headFill: [129, 123, 230, 230],
    width: 4
  },
  '3e89aaa6-15f2-4a8a-a0fb-95f723c36702': {
    label: 'AUV (autonomous) — track 2',
    color: [227, 142, 37, 220],
    headFill: [227, 142, 37, 230],
    width: 3
  },
  'c375714f-a390-4cb7-96c5-afb8e187790d': {
    label: 'AUV (Autonomous Underwater Vehicle)',
    color: [227, 142, 37, 220],
    headFill: [227, 142, 37, 230],
    width: 3
  },
  '436554c6-a314-478c-8ebc-6df48780d1fa': {
    label: 'Exercise contact (track 4)',
    color: [13, 148, 136, 210],
    headFill: [45, 212, 191, 255],
    width: 3
  },
  'b5a5a4c8-5e7d-4bfa-9980-c2d5add5c2c4': {
    label: 'Defense USV-4 (defensive_surface_usv)',
    color: [129, 123, 230, 220],
    headFill: [129, 123, 230, 230],
    width: 4
  },
  '5bbfe35c-23fc-4d03-a3f0-71188de73b6f': {
    label: 'Exercise contact (track 6)',
    color: [8, 145, 178, 210],
    headFill: [125, 211, 252, 255],
    width: 3
  },
  '4537503b-59e4-4a3e-b43b-8b1fe3a8183d': {
    label: 'Exercise contact (track 7)',
    color: [22, 163, 74, 210],
    headFill: [134, 239, 172, 255],
    width: 3
  },
  'a8d4baa1-9c10-4793-81cb-ec7db97860b0': {
    label: 'Exercise contact (track 8)',
    color: [180, 83, 9, 210],
    headFill: [253, 186, 116, 255],
    width: 3
  },
  '9257c775-5d88-4f06-9788-be60ccbd0744': {
    label: 'Exercise contact (track 9)',
    color: [79, 70, 229, 210],
    headFill: [165, 180, 252, 255],
    width: 3
  }
};

/** Unknown-surface / exercise contact that switches to a red pulsing head when near Op Alpha (see MapContainer). */
export const EXERCISE_OP_ALPHA_PROXIMITY_THREAT_VESSEL_ID = '436554c6-a314-478c-8ebc-6df48780d1fa';

/** Inside Op Alpha or within this distance (m) of its boundary counts as "near" for the proximity threat head. */
export const EXERCISE_OP_ALPHA_PROXIMITY_BUFFER_M = 700;

/**
 * Max surface separation (m) to show the convergence ring — only when threat and defense are effectively co-located.
 */
export const EXERCISE_THREAT_DEFENSE_CONVERGENCE_MAX_M = 110;

/** Dashed ring radius (m) around the pair’s midpoint when converged; keep small so it reads as a tight highlight. */
export const EXERCISE_THREAT_DEFENSE_CONVERGENCE_RING_RADIUS_M = 78;

/** Deck.gl polygon rows: fill/line RGBA */
export const SCENARIO_POLYGON_FEATURES = [
  {
    id: 'op-alpha',
    name: 'Op Area Alpha',
    polygon: OP_AREA_ALPHA_RING,
    fill: [239, 68, 68, 45],
    line: [185, 28, 28, 235]
  }
];
